import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MeshHero from '@/components/saas/MeshHero';
import Reveal from '@/components/saas/Reveal';
import MarketingCtaBand from '@/components/saas/MarketingCtaBand';
import { parseMarketingSections, hasRenderableStructure } from '@/lib/parse-marketing-html';
import { getCtaBanner, type HomepageSection } from './helpers';
import ProseFallback from './ProseFallback';

interface Props {
  page: { title?: string; content?: string };
  sections?: HomepageSection[];
  siteName: string;
}

export default function TenantAboutPage({ page, sections, siteName }: Props) {
  const { t } = useTranslation('saas');
  const parsed = useMemo(() => parseMarketingSections(page.content), [page.content]);
  const cta = getCtaBanner(sections);

  const heading = page.title || t('tenantMarketing.aboutSubheadingFallback', { name: siteName });
  const subheading = parsed.intro[0] || t('tenantMarketing.aboutSubheadingFallback', { name: siteName });

  // First intro paragraph is promoted to the hero subheading; the rest become
  // the narrative lede.
  const narrative = parsed.intro.slice(1);
  const structured = hasRenderableStructure(parsed);

  return (
    <>
      <MeshHero heading={heading} subheading={subheading} />

      {!structured ? (
        <ProseFallback html={page.content || ''} />
      ) : (
        <>
          {narrative.length > 0 && (
            <section className="bg-surface">
              <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                <Reveal>
                  <div className="space-y-5">
                    {narrative.map((p, i) => (
                      <p key={i} className="text-lg leading-relaxed text-sage-700">
                        {p}
                      </p>
                    ))}
                  </div>
                </Reveal>
              </div>
            </section>
          )}

          {parsed.sections.length > 0 && (
            <section className="border-t border-sage-200 bg-cream">
              <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {parsed.sections.map((section, idx) => (
                    <Reveal key={`${section.title}-${idx}`} delayMs={(idx % 2) * 80} className="h-full">
                      <article className="flex h-full flex-col rounded-2xl border border-sage-200 bg-surface p-6 lg:p-8">
                        <h2 className="font-display text-xl font-semibold text-sage-900">{section.title}</h2>
                        {section.body.map((p, i) => (
                          <p key={i} className="mt-2 leading-relaxed text-sage-600">
                            {p}
                          </p>
                        ))}
                        {section.bullets.length > 0 && (
                          <ul className="mt-3 list-disc space-y-1 pl-5 text-sage-600">
                            {section.bullets.map((b, i) => (
                              <li key={i}>{b}</li>
                            ))}
                          </ul>
                        )}
                      </article>
                    </Reveal>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <MarketingCtaBand
        heading={cta.heading || t('tenantMarketing.ctaHeadingFallback')}
        subheading={cta.subheading || t('tenantMarketing.ctaSubheadingFallback', { name: siteName })}
        primaryLabel={cta.ctaText || t('tenantMarketing.getStarted')}
        primaryTo={cta.ctaLink || '/signup'}
      />
    </>
  );
}
